import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { BarChart3, Users, FileText, TrendingUp, MessageSquare, Mail } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import SEO from '@/components/SEO';

interface AnalyticsSummary {
  form_type: string;
  total_submissions: number;
  today_submissions: number;
  week_submissions: number;
  month_submissions: number;
}

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState<AnalyticsSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_analytics_summary')
        .select('*');

      if (error) {
        console.error('Error fetching analytics:', error);
        return;
      }

      setAnalytics(data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFormIcon = (formType: string) => {
    switch (formType) {
      case 'contact_submissions':
        return MessageSquare;
      case 'early_adopter_signups':
        return Users;
      case 'newsletter_subscriptions':
        return Mail;
      case 'professional_applications':
        return FileText;
      case 'sponsor_inquiries':
        return TrendingUp;
      case 'story_submissions':
        return FileText;
      default:
        return BarChart3;
    }
  };

  const getFormTitle = (formType: string) => {
    switch (formType) {
      case 'contact_submissions':
        return 'Contact Submissions';
      case 'early_adopter_signups':
        return 'Early Adopter Signups';
      case 'newsletter_subscriptions':
        return 'Newsletter Subscriptions';
      case 'professional_applications':
        return 'Professional Applications';
      case 'sponsor_inquiries':
        return 'Sponsor Inquiries';
      case 'story_submissions':
        return 'Story Submissions';
      default:
        return formType;
    }
  };

  const totalSubmissions = analytics.reduce((sum, item) => sum + item.total_submissions, 0);
  const todayTotal = analytics.reduce((sum, item) => sum + item.today_submissions, 0);
  const weekTotal = analytics.reduce((sum, item) => sum + item.week_submissions, 0);
  const monthTotal = analytics.reduce((sum, item) => sum + item.month_submissions, 0);

  const getFormRoute = (formType: string) => {
    const routes: Record<string, string> = {
      contact_submissions: '/admin/forms/contact',
      early_adopter_signups: '/admin/forms/early-adopters',
      newsletter_subscriptions: '/admin/forms/newsletter',
      professional_applications: '/admin/forms/professionals',
      sponsor_inquiries: '/admin/forms/sponsors',
      story_submissions: '/admin/forms/stories',
    };
    return routes[formType] || '/admin/forms';
  };

  return (
    <>
    <SEO title="Admin Dashboard" description="SeniorThrive admin dashboard overview." noindex />
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Overview of SeniorThrive website performance and form submissions
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Submissions</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{totalSubmissions.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">All time</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{todayTotal}</div>
                <p className="text-xs text-muted-foreground">New submissions today</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{weekTotal}</div>
                <p className="text-xs text-muted-foreground">Last 7 days</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{monthTotal}</div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Form Submissions Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Form Submissions by Type</CardTitle>
          <CardDescription>
            Detailed breakdown of all form submissions across the website
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="p-4 border rounded-lg">
                  <Skeleton className="h-6 w-24 mb-2" />
                  <Skeleton className="h-8 w-16 mb-1" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))
            ) : (
              analytics.map((item) => {
                const Icon = getFormIcon(item.form_type);
                return (
                  <div key={item.form_type} className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-5 w-5 text-primary" />
                      <h3 className="font-medium">{getFormTitle(item.form_type)}</h3>
                    </div>
                    <div className="text-2xl font-bold mb-1">
                      {item.total_submissions.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>Today: {item.today_submissions}</div>
                      <div>This week: {item.week_submissions}</div>
                      <div>This month: {item.month_submissions}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
}